/**
 * Shared helpers for merging Form.io language-dictionary submissions into a flat
 * translation map. Used by the server API route and the client translation service.
 */

export type FormioLanguageSubmission = {
  created?: string;
  modified?: string;
  data?: {
    language?: string;
    translations?: Record<string, unknown>;
  };
};

/**
 * Removes keys whose value is empty or whitespace-only, so they don't
 * override a lower-priority layer when spread during merging.
 */
export function omitEmptyValues(obj: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    if (obj[key]?.trim()) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Flattens a nested object into a single-level object with dot notation keys.
 * Example: { labels: { name: "Name" } } → { "labels.name": "Name" }
 */
export function flattenObject(
  obj: Record<string, unknown>,
  prefix = '',
  rootNamespaces: string[] = ['global']
): Record<string, string> {
  const flattened: Record<string, string> = {};

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;

    const value = obj[key];
    const isRootNamespace = rootNamespaces.includes(key);
    const newPrefix = isRootNamespace ? '' : prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(
        flattened,
        flattenObject(value as Record<string, unknown>, newPrefix, rootNamespaces)
      );
    } else if (typeof value === 'string') {
      const finalKey = newPrefix || key;
      flattened[finalKey] = value;
    }
  }

  return flattened;
}

function getSubmissionTime(submission: FormioLanguageSubmission): number {
  const raw = submission.modified ?? submission.created;
  const time = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Merges translations from every submission that matches the given locale
 * into a single flattened object. Submissions are applied oldest-first and
 * empty values are skipped, so the newest non-empty value for any key wins.
 */
export function mergeSubmissionsForLocale(
  submissions: FormioLanguageSubmission[],
  locale: string
): Record<string, string> {
  const normalizedLocale = locale.toLowerCase();

  const matching = submissions
    .filter(
      (submission) =>
        submission.data?.language?.toLowerCase() === normalizedLocale &&
        submission.data?.translations
    )
    .sort((a, b) => getSubmissionTime(a) - getSubmissionTime(b));

  const merged: Record<string, string> = {};
  for (const submission of matching) {
    Object.assign(
      merged,
      omitEmptyValues(flattenObject(submission.data!.translations as Record<string, unknown>))
    );
  }

  return merged;
}
